/**
 * What a History line calls a field, and how it prints a value — the
 * walkthrough of Oct 30, §8: *"History never shows raw ISO timestamps or column
 * names anywhere."*
 *
 * The audit log is keyed by column (`status_changed_at`), because the log is
 * the record (plan §4.2) — but the tab is read by people, and *Status changed
 * at: 2026-10-30T17:58:38.67893+00:00* is a database talking. So every column
 * of every table a record page's History asks for is either **named** here,
 * in the words that record's own form and About card use, or **left out**
 * because it is bookkeeping the line already says another way (who made the
 * change, when, a derived copy of another field). `tests/history-words.test.ts`
 * reads those tables' columns from the database and fails on one that is
 * neither, so a new column cannot quietly print its own name.
 *
 * Pure — no React — so the test can import it.
 */
import { PREFERS_LABELS } from '@rowted/shared'

/** Never printed on any table: bookkeeping, identity, derived copies. */
const HIDDEN = new Set([
  'id', 'tenant_id', 'created_at', 'created_by', 'updated_at', 'updated_by',
  'deleted_at', 'deleted_by', 'version', 'address_key', 'erased_at', 'erased_label',
  /* A move is told by the status itself (and, on a job, by its own row). */
  'status_changed_at', 'status_changed_by',
  /* Derived from the address, or from another column on the same row. */
  'latitude', 'longitude', 'phone_digits', 'state_unrecognized', 'measured_areas',
  /* Where a row came from, which nobody changes. */
  'reference', 'job_id', 'quote_id', 'quote_line_id', 'quote_visit_id', 'quote_approval_id',
  'kit_id', 'request_id', 'invoice_id', 'map_id', 'layer_id', 'identity_id', 'made_by',
  'added_from', 'origin', 'quick_check_id', 'late_fee_for_id', 'billing_schedule_id',
  'milestone_id', 'merged_into_id', 'pay_token_hash',
  'pay_token_issued_at', 'view', 'geometry', 'shape', 'sort_order',
  /* Kept by the system as things happen; the line's own time says it. */
  'last_login_at', 'last_sign_in_at', 'reminder_step', 'next_reminder_on', 'payment_pending',
  'bounced', 'sms_ok_number',
])

/** Every table's own words — the form's label, else the About card's. */
const SHARED: Record<string, string> = {
  name: 'Name',
  title: 'Title',
  description: 'Name',
  status: 'Status',
  status_reason: 'Why',
  note: 'Note',
  notes: 'Notes',
  email: 'Email',
  phone: 'Phone',
  org_unit_id: 'Branch',
  account_id: 'Account',
  property_id: 'Property',
  contact_id: 'Contact',
  address_line1: 'Street',
  address_line2: 'Unit',
  city: 'City',
  state: 'State',
  postal_code: 'ZIP',
  country: 'Country',
  colour: 'Colour',
  color: 'Colour',
  kind: 'Kind',
  role: 'Role',
  source: 'Came from',
  qty: 'Quantity',
  rate: 'Rate',
  amount: 'Amount',
  taxable: 'Taxable',
  tax_rate: 'Tax rate',
  tax_amount: 'Tax',
  photo_file_id: 'Photo',
  sitemap_file_id: 'Site plan',
  closed_at: 'Closed',
  notification_prefs: 'Notifications',
}

const TABLE_WORDS: Record<string, Record<string, string>> = {
  jobs: {
    service_line: 'Division',
    job_type: 'Type',
    salesperson_id: 'Salesperson',
    reviewed_at: 'Reviewed',
    reviewed_reason: 'Reviewed — why',
    rework_open: 'Returned',
    lead_source_id: 'Lead source',
    wanted_kind: 'When they want it',
    wanted_start: 'Wanted from',
    wanted_end: 'Wanted by',
    wanted_label: 'When they want it',
    budget_low: 'Budget from',
    budget_high: 'Budget up to',
  },
  contacts: {
    first_name: 'First name',
    last_name: 'Last name',
    account_id: 'Company',
    phone: 'Main phone',
    mobile_phone: 'Mobile',
    home_phone: 'Home',
    office_phone: 'Office',
    preferred_channel: 'Prefers',
    contact_hours: 'Best time',
    notification_defaults: 'Notifications',
    active: 'Active',
    sms_ok_at: 'OK to text',
    sms_ok_scope: 'OK to text — for',
    sms_ok_source: 'OK to text — asked by',
    sms_opted_out_at: 'Stopped texts',
  },
  properties: {
    access_notes: 'Access notes',
    staging_notes: 'Staging notes',
    zone_id: 'Zone',
    property_type: 'Type',
    customer_since: 'Customer since',
    tax_jurisdiction: 'Tax area',
  },
  contact_property_links: {
    is_primary: 'Primary',
    is_bill_to: 'Bill to',
    approves_quotes: 'Approves quotes',
    access_note: 'Access note',
  },
  portal_access: {
    permissions: 'Can do',
    invited_at: 'Invited',
    revoked_at: 'Revoked',
  },
  work_items: {
    budgeted_hours: 'Planned',
    value: 'Value',
    earliest_on: 'From',
    due_on: 'Due',
    requires: 'Needs',
    held_until: 'Held until',
    hold_reason: 'Why it is held',
    held_at: 'Put on hold',
    trigger: 'Starts',
    series_index: 'Visit of the series',
    series_count: 'Visits in the series',
    state: 'Status',
    cancel_reason: 'Why it was cancelled',
    cancelled_at: 'Cancelled',
    billing_kind: 'Billing',
    bill_rate: 'Bill rate',
    crew_note: 'Note for the crew',
    close_note: 'Close-out note',
    labor_cost_rate: 'Labor rate',
  },
  visits: {
    work_item_id: 'Visit',
    scheduled_date: 'Day',
    crew_id: 'Crew',
    yard_id: 'Yard',
    sequence: 'Day of the visit',
    hours_for_day: 'Hours',
    completed_at: 'Closed out',
    is_final_visit: 'Last day',
    added_reason: 'Why it was added',
  },
  invoices: {
    bill_to_contact_id: 'Bill to',
    issue_date: 'Issued',
    due_date: 'Due',
    terms_days: 'Terms',
    subtotal: 'Subtotal',
    tax_total: 'Tax',
    total: 'Total',
    sent_at: 'Sent',
    viewed_at: 'Viewed',
    in_dispute: 'In dispute',
    period_start: 'Period from',
    period_end: 'Period to',
    po_number: 'PO number',
  },
  invoice_lines: {
    visit_id: 'Visit',
  },
  equipment: {
    equipment_type_id: 'Type',
    asset_tag: 'Asset tag',
    internal_hourly_rate: 'Cost per hour',
    paired_with_id: 'Paired with',
    billable_rate: 'Bill rate',
    billable_unit: 'Billed by',
    year: 'Year',
    make: 'Make',
    model: 'Model',
    vin: 'VIN',
    plate: 'Plate',
    plate_expires_on: 'Plate expires',
    gvwr_lb: 'GVWR',
    curb_weight_lb: 'Curb weight',
    decaled_on: 'Decaled',
    purchased_on: 'Purchased',
    purchase_cost: 'Purchase cost',
    salvage_value: 'Salvage value',
    tax_method: 'Tax depreciation',
    book_method: 'Book depreciation',
    book_life_months: 'Book life',
    home_yard_id: 'Home yard',
    service_template_id: 'Service plan',
    customer_name: 'Customer-facing name',
  },
  work_orders: {
    equipment_id: 'Equipment',
    detail: 'Detail',
    service_line_id: 'Service',
    assigned_to: 'Assigned to',
    scheduled_for: 'Scheduled for',
    takes_out_of_service: 'Out of service',
    labor_hours: 'Labor hours',
    parts_cost: 'Parts cost',
    meter_at_open: 'Meter',
    opened_at: 'Opened',
  },
  users: {
    first_name: 'First name',
    last_name: 'Last name',
    password_hash: 'Password',
    totp_secret: 'Two-step sign-in',
    mfa_enrolled_at: 'Two-step sign-in',
    home_org_unit_id: 'Home branch',
    password_changed_at: 'Password',
    day_starts_at: 'Day starts at',
    day_address: 'Day starts from',
    day_latitude: 'Day starts from',
    day_longitude: 'Day starts from',
    work_days: 'Work days',
    work_start: 'Starts work',
    work_end: 'Finishes work',
  },
  property_maps: {
    base: 'Base map',
    archived_at: 'Archived',
  },
  property_map_layers: {
    audience: 'Who sees it',
    division_ids: 'Divisions',
    owner_user_id: 'Owner',
    pinned_to_tickets: 'Pinned to visits',
    crew_can_add: 'Crew can add',
    hidden_on_tickets: 'Hidden on visits',
  },
  property_map_features: {
    glyph: 'Icon',
    area_sqft: 'Area',
    length_ft: 'Length',
  },
}

/* A visit was a ticket until migration 0102, and the log keeps the old name. */
TABLE_WORDS.tickets = TABLE_WORDS.visits!

/** Columns whose value is a key — *in_progress* prints as *In progress*. */
const KEYED = new Set([
  'status', 'kind', 'role', 'source', 'state', 'preferred_channel', 'property_type',
  'billing_kind', 'trigger', 'sms_ok_scope', 'sms_ok_source', 'audience', 'base',
  'billable_unit', 'tax_method', 'book_method', 'wanted_kind', 'service_line', 'job_type',
])

/** Columns whose value is money, sent by Postgres as a decimal string. */
const MONEY = new Set([
  'value', 'rate', 'amount', 'tax_amount', 'subtotal', 'tax_total', 'total', 'bill_rate',
  'labor_cost_rate', 'internal_hourly_rate', 'billable_rate', 'purchase_cost', 'salvage_value',
  'parts_cost', 'budget_low', 'budget_high',
])

/**
 * The words a History line uses for a column, or `null` when the column is
 * never printed. `undefined` — a column nobody has named — falls back to the
 * column made readable, which the test refuses for every table above.
 */
export function historyFieldLabel(table: string, column: string): string | null | undefined {
  if (HIDDEN.has(column) || column.endsWith('_by')) return null
  return TABLE_WORDS[table]?.[column] ?? SHARED[column]
}

/** Every table whose History the app draws — the test reads their columns. */
export const HISTORY_TABLES = Object.keys(TABLE_WORDS).filter((table) => table !== 'tickets')

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const MOMENT = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/
const DAY = /^\d{4}-\d{2}-\d{2}$/
const KEY = /^[a-z]+(_[a-z]+)*$/

/** *in_progress* → *In progress*. */
export const keyWords = (key: string): string =>
  key.replace(/_/g, ' ').replace(/^./, (character) => character.toUpperCase())

/** A reference (another row's id) has no words of its own — the line says it changed. */
export const isReference = (value: unknown): boolean => typeof value === 'string' && UUID.test(value)

/** What kind of value a History line is about to print, so the tab can format it. */
export type HistoryValue =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'moment'; iso: string }
  | { kind: 'day'; iso: string }
  | { kind: 'money'; amount: string }

/**
 * A value, classified — never a raw ISO stamp, never an id. The tab turns a
 * moment, a day or an amount into words with `lib/format.ts`; this module stays
 * free of `Intl` so it is the same in a test and in a browser.
 */
export function historyValue(column: string, value: unknown): HistoryValue {
  if (value === null || value === undefined || value === '') return { kind: 'empty' }
  if (typeof value === 'boolean') return { kind: 'text', text: value ? 'yes' : 'no' }
  if (typeof value === 'number') {
    return MONEY.has(column) ? { kind: 'money', amount: String(value) } : { kind: 'text', text: String(value) }
  }
  if (typeof value !== 'string') return { kind: 'text', text: 'a new value' }
  if (MOMENT.test(value)) return { kind: 'moment', iso: value }
  if (DAY.test(value)) return { kind: 'day', iso: value }
  if (MONEY.has(column) && /^-?\d+(\.\d+)?$/.test(value)) return { kind: 'money', amount: value }
  /* The contact form's own words for a channel — *Text*, not *Sms*. */
  if (column === 'preferred_channel' && value in PREFERS_LABELS) {
    return { kind: 'text', text: PREFERS_LABELS[value as keyof typeof PREFERS_LABELS] }
  }
  if (KEYED.has(column) && KEY.test(value)) return { kind: 'text', text: keyWords(value) }
  return { kind: 'text', text: value }
}
